import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "horizontal" | "compact";
}

export default function ArticleCard({
  article,
  variant = "default",
}: ArticleCardProps) {
  if (variant === "horizontal") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 items-start p-3 rounded-lg hover:bg-background-100 transition-colors"
      >
        <div className="w-32 h-20 sm:w-40 sm:h-24 rounded-md overflow-hidden flex-shrink-0">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors mb-1">
            {article.title}
          </h3>
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <span>{article.categoryName}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300" />
            <span>{article.readCount.toLocaleString()} 阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block py-2 border-b border-background-200 last:border-b-0"
      >
        <h3 className="text-sm text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
          <span>{article.publishDate}</span>
          <span className="w-1 h-1 rounded-full bg-foreground-300" />
          <span>{(article.readCount / 10000).toFixed(1)}万阅读</span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-all duration-300 hover:-translate-y-1"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {article.isHot && (
          <span className="absolute top-3 left-3 px-2 py-1 bg-primary-500 text-background-50 text-xs font-bold rounded-md">
            热
          </span>
        )}
        <span className="absolute top-3 right-3 px-2 py-1 bg-background-950/70 backdrop-blur-sm text-background-50 text-xs rounded-md">
          {article.categoryName}
        </span>
      </div>
      <div className="p-4">
        <h3 className="text-base font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3">
          {article.summary}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <div className="flex items-center gap-2">
            <span>{article.publishDate}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300" />
            <span>{article.author}</span>
          </div>
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-accent-500" />
            {article.hotScore}
          </span>
        </div>
      </div>
    </Link>
  );
}